import React, { useState, useRef, useEffect, useMemo } from 'react';
import { LuSend, LuPaperclip, LuMic, LuTrash2, LuImage, LuX, LuLock, LuSmile, LuFile, LuZap, LuTriangleAlert } from 'react-icons/lu';
import EmojiPicker, { type EmojiClickData, Theme } from 'emoji-picker-react';
import './MessageInput.css';
import type { Atalho } from '../services/AtalhoService';
import { paraBusca, type AtalhoResolvido } from '../lib/atalhos';

interface MessageInputProps {
  onSendMessage: (text: string) => void;
  onSendMedia?: (file: File, type: 'image' | 'audio' | 'file') => Promise<void>;
  disabled?: boolean;
  disabledMessage?: string;
  /** Nome de quem recebe, só para o placeholder dizer para quem se escreve. */
  destinatario?: string;
  /** Quando muda (ex.: id da conversa aberta), o cursor vai para a caixa de mensagem. */
  focoKey?: string | number;
  /**
   * Atalhos de mensagem ("/orcamento"). Chegam de fora porque este compositor é usado em duas telas e
   * só o Chat os tem: sem `atalhos` e `resolverAtalho`, não existe "/" nem botão de raio.
   * Só o compositor normal os usa — desconectado, janela fechada e gravando não têm campo de texto.
   */
  atalhos?: Atalho[];
  /** Troca as variáveis pelo que a conversa aberta sabe; variável sem valor volta escrita e listada. */
  resolverAtalho?: (atalho: Atalho) => AtalhoResolvido;
  /** Chamado quando o atalho entra na caixa (a contagem de uso é por inserção, não por envio). */
  aoUsarAtalho?: (atalho: Atalho) => void;
  /** Rodapé do popover: leva ao cadastro. */
  aoGerenciarAtalhos?: () => void;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onSendMedia,
  disabled,
  disabledMessage,
  destinatario,
  focoKey,
  atalhos,
  resolverAtalho,
  aoUsarAtalho,
  aoGerenciarAtalhos,
}) => {
  const [text, setText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  /* Atalhos: o popover abre de dois jeitos — "/" como PRIMEIRO caractere (caminho rápido) e o botão de
     raio (caminho que se descobre sozinho). Esc fecha e deixa o texto como está. */
  const temAtalhos = !!atalhos && !!resolverAtalho;
  const [botaoAberto, setBotaoAberto] = useState(false);
  const [barraFechada, setBarraFechada] = useState(false);
  const [indiceAtalho, setIndiceAtalho] = useState(0);
  const [semValor, setSemValor] = useState<string[]>([]);
  const popoverRef = useRef<HTMLDivElement>(null);
  const raioRef = useRef<HTMLButtonElement>(null);
  /* Comando não tem espaço: "/orc" abre, "/orc " já virou texto comum. */
  const barraAtiva = temAtalhos && /^\/\S*$/.test(text) && !barraFechada;
  const popoverAberto = temAtalhos && (botaoAberto || barraAtiva);
  const consulta = botaoAberto ? '' : (barraAtiva ? text.slice(1) : '');

  const atalhosFiltrados = useMemo(() => {
    if (!atalhos) return [];
    const q = paraBusca(consulta.trim());
    if (!q) return atalhos;
    const acha = atalhos.filter(a => paraBusca(a.comando).includes(q) || paraBusca(a.titulo).includes(q));
    /* Quem começa pelo que foi digitado vem antes; a ordem original (os meus primeiro) é mantida. */
    return [
      ...acha.filter(a => paraBusca(a.comando).startsWith(q)),
      ...acha.filter(a => !paraBusca(a.comando).startsWith(q)),
    ];
  }, [atalhos, consulta]);

  /* O "/" digitado de novo depois de apagar reabre: a trava do Esc só vale enquanto o texto for "/algo". */
  useEffect(() => {
    if (barraFechada && !/^\/\S*$/.test(text)) setBarraFechada(false);
  }, [text, barraFechada]);

  useEffect(() => { setIndiceAtalho(0); }, [consulta, botaoAberto, popoverAberto]);

  /* Um popover por vez: abrir os atalhos fecha o emoji. */
  useEffect(() => {
    if (popoverAberto) setShowEmojiPicker(false);
  }, [popoverAberto]);

  useEffect(() => {
    if (!popoverAberto) return;
    popoverRef.current?.querySelector('.is-marcado')?.scrollIntoView({ block: 'nearest' });
  }, [indiceAtalho, popoverAberto, atalhosFiltrados]);

  useEffect(() => {
    if (!popoverAberto) return;
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (popoverRef.current?.contains(alvo) || raioRef.current?.contains(alvo)) return;
      setBotaoAberto(false);
      setBarraFechada(true);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [popoverAberto]);

  const fecharAtalhos = () => {
    setBotaoAberto(false);
    setBarraFechada(true);
  };

  /* Entra na caixa com as variáveis já resolvidas e o cursor no fim. NÃO envia: a pessoa lê, ajusta e
     manda. Com o "/", o texto digitado ("/orc") é substituído; com o botão, entra no ponto do cursor. */
  const inserirAtalho = (atalho: Atalho) => {
    if (!resolverAtalho) return;
    const { texto, semValor: faltou } = resolverAtalho(atalho);
    const campo = textareaRef.current;
    const porBotao = botaoAberto;
    const ini = porBotao ? (campo?.selectionStart ?? text.length) : 0;
    const fim = porBotao ? (campo?.selectionEnd ?? text.length) : text.length;
    const novo = text.slice(0, ini) + texto + text.slice(fim);

    setText(novo);
    setBotaoAberto(false);
    setBarraFechada(true);
    setSemValor(faltou);
    aoUsarAtalho?.(atalho);

    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      /* Variável sem valor: seleciona a primeira, para a próxima tecla já trocar o buraco por texto. */
      const buraco = faltou.length ? novo.indexOf('{{' + faltou[0] + '}}', ini) : -1;
      if (buraco >= 0) ta.setSelectionRange(buraco, buraco + faltou[0].length + 4);
      else ta.setSelectionRange(ini + texto.length, ini + texto.length);
    });
  };

  /* O aviso some sozinho quando a pessoa troca todos os buracos. */
  const pendentes = semValor.filter(v => text.includes('{{' + v + '}}'));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Abriu a conversa: já dá para digitar, sem clicar na caixa. `disabled` entra na
  // dependência porque a caixa só existe destravada (ex.: WhatsApp reconectou).
  useEffect(() => {
    if (focoKey === undefined || disabled) return;
    textareaRef.current?.focus({ preventScroll: true });
  }, [focoKey, disabled]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 108)}px`;
    }
  }, [text]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    if (disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      alert('Não foi possível acessar o microfone.');
    }
  };

  const stopRecording = (shouldSend: boolean) => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.onstop = async () => {
        if (shouldSend && onSendMedia) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const audioFile = new File([audioBlob], `audio-${Date.now()}.webm`, { type: 'audio/webm' });
          await onSendMedia(audioFile, 'audio');
        }
        mediaRecorderRef.current?.stream.getTracks().forEach(track => track.stop());
      };
      mediaRecorderRef.current.stop();
    }

    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]);
    e.target.value = '';
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (disabled) return;
    if (e.clipboardData.files && e.clipboardData.files.length > 0) {
      const file = e.clipboardData.files[0];
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        e.preventDefault();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    /* O popover vem ANTES do envio: Enter com a lista aberta insere o atalho, não manda a mensagem.
       Sem nenhum atalho na lista o Enter segue normal (a mensagem "/algo" pode ser texto de verdade). */
    if (popoverAberto) {
      if (e.key === 'Escape') {
        e.preventDefault();
        fecharAtalhos();
        return;
      }
      if (atalhosFiltrados.length > 0) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const passo = e.key === 'ArrowDown' ? 1 : -1;
          setIndiceAtalho(i => (i + passo + atalhosFiltrados.length) % atalhosFiltrados.length);
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          inserirAtalho(atalhosFiltrados[Math.min(indiceAtalho, atalhosFiltrados.length - 1)]);
          return;
        }
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    setShowEmojiPicker(false);

    if (selectedFile && onSendMedia) {
      const type = selectedFile.type.startsWith('image/') ? 'image' : 'file';
      const fileToSend = selectedFile;
      setSelectedFile(null);
      try {
        await onSendMedia(fileToSend, type);
      } catch (error) {
        console.error('Error sending media', error);
      }
    }

    if (text.trim()) {
      onSendMessage(text);
      setText('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
  };

  const onEmojiClick = (emoji: EmojiClickData) => setText(prev => prev + emoji.emoji);

  const temaEmoji =
    document.documentElement.getAttribute('data-theme') === 'dark' ? Theme.DARK : Theme.LIGHT;

  /* WhatsApp desconectado: o compositor explica onde ele está travado, em vez
     de a tela inteira abrir uma modal. */
  if (disabled) {
    return (
      <div className="ch-cmp">
        <div className="ch-cmp-travado">
          <LuLock size={14} />
          {disabledMessage || 'Não dá para enviar agora.'}
        </div>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="ch-cmp">
        <div className="ch-gravando">
          <LuMic size={15} />
          Gravando… {formatTime(recordingTime)}
        </div>
        <button type="button" className="ch-bt ico" onClick={() => stopRecording(false)} title="Descartar">
          <LuTrash2 size={15} />
        </button>
        <button type="button" className="ch-enviar" onClick={() => stopRecording(true)} title="Enviar áudio">
          <LuSend size={16} />
        </button>
      </div>
    );
  }

  return (
    <form className="ch-cmp" onSubmit={handleSubmit}>
      <input
        type="file"
        ref={imageInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        style={{ display: 'none' }}
      />
      <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} />

      <div className="ch-cmp-col">
        {selectedFile && (
          <div className="ch-cmp-anexo">
            {selectedFile.type.startsWith('image/') ? (
              <img src={URL.createObjectURL(selectedFile)} alt="Prévia do anexo" />
            ) : (
              <LuFile size={20} />
            )}
            <span className="ch-item-txt">{selectedFile.name}</span>
            <button type="button" className="ch-cmp-ico" onClick={() => setSelectedFile(null)} title="Remover anexo">
              <LuX size={15} />
            </button>
          </div>
        )}

        {pendentes.length > 0 && (
          <div className="ch-cmp-aviso" role="status">
            <LuTriangleAlert size={13} />
            <span>
              Sem valor para {pendentes.map(v => '{{' + v + '}}').join(', ')}. Troque no texto antes de enviar.
            </span>
          </div>
        )}

        <div className="ch-cmp-campo">
          <textarea
            id="ch-mensagem"
            ref={textareaRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            aria-controls={popoverAberto ? 'ch-atalhos-lista' : undefined}
            aria-activedescendant={popoverAberto && atalhosFiltrados.length ? `ch-atalho-${indiceAtalho}` : undefined}
            /* A dica do "/" vive aqui porque o campo vazio é o único momento em que ela
               não atrapalha: some assim que a pessoa começa a escrever. Sem ela o atalho
               ficava invisível — nem o assistente sabia que existia (12/10/2026). */
            placeholder={destinatario ? `Mensagem para ${destinatario} — "/" abre os atalhos` : 'Mensagem — "/" abre os atalhos'}
            rows={1}
          />
          <span className="ch-cmp-icos">
            <button type="button" className="ch-cmp-ico" onClick={() => { setShowEmojiPicker(a => !a); setBotaoAberto(false); setBarraFechada(true); }} title="Emoji">
              <LuSmile size={17} />
            </button>
            <button type="button" className="ch-cmp-ico" onClick={() => imageInputRef.current?.click()} title="Enviar imagem">
              <LuImage size={17} />
            </button>
            <button type="button" className="ch-cmp-ico" onClick={() => fileInputRef.current?.click()} title="Enviar arquivo">
              <LuPaperclip size={17} />
            </button>
            {temAtalhos && (
              <button
                ref={raioRef}
                type="button"
                className={`ch-cmp-ico${botaoAberto ? ' is-aberto' : ''}`}
                onClick={() => {
                  setShowEmojiPicker(false);
                  setBarraFechada(true);
                  setBotaoAberto(a => !a);
                  textareaRef.current?.focus();
                }}
                title="Atalhos de mensagem"
                aria-label="Atalhos de mensagem"
                aria-haspopup="listbox"
                aria-expanded={popoverAberto}
              >
                <LuZap size={17} />
              </button>
            )}
          </span>
        </div>
      </div>

      {text.trim() || selectedFile ? (
        <button type="submit" className="ch-enviar" title="Enviar">
          <LuSend size={16} />
        </button>
      ) : (
        <button type="button" className="ch-enviar" onClick={startRecording} title="Gravar áudio">
          <LuMic size={16} />
        </button>
      )}

      {popoverAberto && (
        <div ref={popoverRef} className="ch-atalhos" role="presentation">
          <div className="ch-atalhos-topo">
            {botaoAberto || !consulta ? 'Atalhos' : <>Atalhos · "{consulta}"</>}
          </div>
          <div id="ch-atalhos-lista" className="ch-atalhos-lista" role="listbox" aria-label="Atalhos de mensagem">
            {atalhosFiltrados.length === 0 ? (
              <div className="ch-atalhos-vazio">
                {atalhos && atalhos.length > 0
                  ? <>Nenhum atalho com "{consulta}". Enter envia o texto como está.</>
                  : 'Você ainda não tem atalhos. Crie o primeiro em Gerenciar atalhos.'}
              </div>
            ) : atalhosFiltrados.map((a, i) => (
              <div
                key={a.atalhoId}
                id={`ch-atalho-${i}`}
                role="option"
                aria-selected={i === indiceAtalho}
                className={`ch-atalho${i === indiceAtalho ? ' is-marcado' : ''}`}
                /* Não tira o foco da caixa de texto: o teclado continua valendo. */
                onMouseDown={e => e.preventDefault()}
                onMouseMove={() => i !== indiceAtalho && setIndiceAtalho(i)}
                onClick={() => inserirAtalho(a)}
              >
                <span className="ch-atalho-cima">
                  <span className="ch-atalho-cmd">/{a.comando}</span>
                  <span className="ch-atalho-tit">{a.titulo}</span>
                  <span className="ch-atalho-dono">{a.daEmpresa ? 'Empresa' : 'Só eu'}</span>
                </span>
                <span className="ch-atalho-corpo">{a.corpo}</span>
              </div>
            ))}
          </div>
          <div className="ch-atalhos-pe">
            <span className="ch-atalhos-teclas">↑↓ navegar · Enter inserir · Esc fechar</span>
            {aoGerenciarAtalhos && (
              <button type="button" className="ch-atalhos-gerir" onClick={aoGerenciarAtalhos}>
                Gerenciar atalhos
              </button>
            )}
          </div>
        </div>
      )}

      {showEmojiPicker && (
        <div ref={emojiPickerRef} className="ch-emoji">
          <EmojiPicker onEmojiClick={onEmojiClick} theme={temaEmoji} width={320} height={380} />
        </div>
      )}
    </form>
  );
};

export default MessageInput;
